// src/components/NotificationBell.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Package, Receipt, FileBarChart, X, Clock, Settings, Truck, CheckCircle2, Store, ShoppingBag } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../utils/supabase';


const DISMISSED_KEY = 'nexus_dismissed_notifications';
const STALE_COUNT_KEY = 'nexus_stale_inventory_count';

// Get today's date string for comparison
const getTodayString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

// Load dismissed notifications from localStorage (only for today)
const loadDismissed = (userId) => {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    const userData = parsed[userId];
    if (!userData || userData.date !== getTodayString()) {
      return {};
    }
    return userData.dismissed || {};
  } catch {
    return {};
  }
};

// Save dismissed notifications to localStorage
const saveDismissed = (userId, dismissed) => {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    parsed[userId] = {
      date: getTodayString(),
      dismissed: dismissed
    };
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(parsed));
  } catch (err) {
    console.error('Error saving dismissed notifications:', err);
  }
};

// Load the last known stale inventory count
const loadStaleCount = (userId) => {
  try {
    const stored = localStorage.getItem(STALE_COUNT_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed[userId] ?? null;
  } catch {
    return null;
  }
};

// Save the current stale inventory count
const saveStaleCount = (userId, count) => {
  try {
    const stored = localStorage.getItem(STALE_COUNT_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    parsed[userId] = count;
    localStorage.setItem(STALE_COUNT_KEY, JSON.stringify(parsed));
  } catch (err) {
    console.error('Error saving stale count:', err);
  }
};

// Check if today is report day (Sunday = 0, Monday = 1)
const isReportDay = () => {
  const today = new Date().getDay();
  return today === 0 || today === 1; // Sunday or Monday
};

// Get start of the week (last Sunday)
const getWeekStart = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? 7 : dayOfWeek; // If Sunday, go back 7 days to previous Sunday
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - diff);
  weekStart.setHours(0, 0, 0, 0);
  return weekStart;
};

const NotificationBell = ({ isCollapsed = false, isMobile = false, isHeader = false }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dismissedLoaded, setDismissedLoaded] = useState(false);
  const [sessionDismissed, setSessionDismissed] = useState({});
  const menuRef = useRef(null);

  // Load dismissed state from localStorage on mount
  useEffect(() => {
    if (currentUser?.uid) {
      const saved = loadDismissed(currentUser.uid);
      setSessionDismissed(saved);
    }
    setDismissedLoaded(true);
  }, [currentUser?.uid]);

  // Save dismissed state to localStorage whenever it changes
  useEffect(() => {
    if (currentUser?.uid && Object.keys(sessionDismissed).length > 0) {
      saveDismissed(currentUser.uid, sessionDismissed);
    }
  }, [currentUser?.uid, sessionDismissed]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch notifications function
  const fetchNotifications = useCallback(async () => {
    if (!currentUser?.uid || !dismissedLoaded) {
      setLoading(false);
      return;
    }

    try {
      const notifs = [];

      // Try to get user preferences, but default to showing all notifications
      let prefs = {
        emailSales: true,
        emailInventory: true,
        emailReports: true,
        packagesMode: 'in_app',          // tri-state: off | in_app | email
        renewalsMode: 'off',             // tri-state: off | in_app | email
        monthlySummaryMode: 'off',       // tri-state: off | in_app | email
      };
      try {
        const { data: settings, error } = await supabase
          .from('user_settings')
          .select('notification_sales, notification_inventory, notification_reports, notification_packages_mode, notification_renewals_mode, notification_monthly_summary_mode')
          .eq('user_id', currentUser.uid)
          .single();

        if (!error && settings) {
          // Only override if the columns exist and have values
          if (settings.notification_sales !== null && settings.notification_sales !== undefined) {
            prefs.emailSales = settings.notification_sales;
          }
          if (settings.notification_inventory !== null && settings.notification_inventory !== undefined) {
            prefs.emailInventory = settings.notification_inventory;
          }
          if (settings.notification_reports !== null && settings.notification_reports !== undefined) {
            prefs.emailReports = settings.notification_reports;
          }
          if (settings.notification_packages_mode) {
            prefs.packagesMode = settings.notification_packages_mode;
          }
          if (settings.notification_renewals_mode) {
            prefs.renewalsMode = settings.notification_renewals_mode;
          }
          if (settings.notification_monthly_summary_mode) {
            prefs.monthlySummaryMode = settings.notification_monthly_summary_mode;
          }
        }
      } catch (prefErr) {
        // If preferences can't be loaded, show all notifications by default
      }

      // Check for stale inventory (items still in stock for >7 days).
      // Uses purchase_date when present, falls back to created_at for legacy
      // rows where the user never recorded a purchase date. Excludes anything
      // already marked sold so the count reflects what's *actually* sitting.
      if (prefs.emailInventory) {
        const thresholdDate = new Date();
        thresholdDate.setDate(thresholdDate.getDate() - 7);
        const thresholdDateStr = thresholdDate.toISOString().split('T')[0]; // YYYY-MM-DD for date column
        const thresholdIso = thresholdDate.toISOString();

        const { count, error } = await supabase
          .from('inventory')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', currentUser.uid)
          .neq('status', 'sold')
          .or(
            `purchase_date.lt.${thresholdDateStr},and(purchase_date.is.null,created_at.lt.${thresholdIso})`
          );

        if (!error && count > 0) {
          const lastKnownCount = loadStaleCount(currentUser.uid);

          // Check if notification was dismissed
          const isDismissed = sessionDismissed['stale-inventory'];

          // Show notification if:
          // 1. Not dismissed, OR
          // 2. Count has increased since last check (new items became stale)
          const hasNewStaleItems = lastKnownCount !== null && count > lastKnownCount;
          const shouldShow = !isDismissed || hasNewStaleItems;

          // Always update the stored count
          saveStaleCount(currentUser.uid, count);

          if (shouldShow) {
            // If we have new stale items, clear the dismissal
            if (hasNewStaleItems && isDismissed) {
              setSessionDismissed(prev => {
                const { 'stale-inventory': _, ...rest } = prev;
                if (currentUser?.uid) {
                  saveDismissed(currentUser.uid, rest);
                }
                return rest;
              });
            }

            notifs.push({
              id: 'stale-inventory',
              type: 'inventory',
              icon: Package,
              title: 'Stale Inventory',
              message: `${count} item${count > 1 ? 's' : ''} in stock over 7 days`,
              severity: count > 10 ? 'warning' : 'info',
              link: '/inventory',
            });
          }
        } else if (!error) {
          // No stale items, reset the count
          saveStaleCount(currentUser.uid, 0);
        }
      }

      // Pull recent (non-dismissed) package status notifications. These
      // are real, persistent DB rows inserted by the shippo-webhook the
      // moment a Shippo update arrives — so the bell can show "delivered"
      // within seconds of the carrier scan. Dismissal also persists to
      // the DB (sets dismissed_at) so dismissals survive reloads + devices.
      if (prefs.packagesMode !== 'off') {
        const { data: pkgNotifs, error: pkgErr } = await supabase
          .from('package_notifications')
          .select('id, event_type, package_name, carrier, tracking_number, package_id, created_at')
          .eq('user_id', currentUser.uid)
          .is('dismissed_at', null)
          .order('created_at', { ascending: false })
          .limit(20);

        if (!pkgErr && pkgNotifs) {
          // Per-event display config so the bell reads at a glance.
          const EVENT_META = {
            shipped:          { icon: Truck,          title: 'Shipped',          severity: 'info' },
            out_for_delivery: { icon: Truck,          title: 'Out for delivery', severity: 'info' },
            delivered:        { icon: CheckCircle2,   title: 'Delivered',        severity: 'success' },
            // Store pickups carry their own event types rather than reusing
            // out_for_delivery/delivered, because this bell renders from
            // event_type alone — package_notifications stores no title.
            ready_for_pickup: { icon: Store,          title: 'Ready for pickup', severity: 'info' },
            picked_up:        { icon: ShoppingBag,    title: 'Picked up',        severity: 'success' },
          };

          for (const n of pkgNotifs) {
            const meta = EVENT_META[n.event_type] || { icon: Package, title: 'Package update', severity: 'info' };
            const itemLabel = n.package_name || n.tracking_number || 'Package';
            const carrierLabel = n.carrier ? n.carrier.toUpperCase() : '';
            notifs.push({
              // Prefix with `package-` so dismiss() can route the X click
              // to the DB instead of (or in addition to) localStorage.
              id: `package-${n.id}`,
              dbId: n.id, // used by the DB-backed dismiss path
              type: 'package',
              icon: meta.icon,
              title: meta.title,
              message: carrierLabel ? `${itemLabel} · ${carrierLabel}` : itemLabel,
              severity: meta.severity,
              link: n.package_id ? `/orders-tracking?package=${n.package_id}` : '/orders-tracking',
            });
          }
        } else if (pkgErr) {
          console.warn('[NotificationBell] package_notifications query failed:', pkgErr.message);
        }
      }

      // Upcoming renewals — same lookup the weekly cron uses, but read
      // straight to the bell for live in-app visibility. Mirrors what
      // send-email-notifications → fetchUpcomingRenewals does:
      // recurring + active expenses with next_due_date in [today, +7d].
      if (prefs.renewalsMode !== 'off') {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
          .toISOString().split('T')[0];

        const { data: upcomingRenewals, error: renewalErr } = await supabase
          .from('expenses')
          .select('price, tax, next_due_date, description')
          .eq('user_id', currentUser.uid)
          .eq('is_recurring', true)
          .eq('status', 'active')
          .gte('next_due_date', todayStr)
          .lte('next_due_date', weekFromNow)
          .order('next_due_date', { ascending: true });

        if (!renewalErr && upcomingRenewals && upcomingRenewals.length > 0) {
          const total = upcomingRenewals.reduce(
            (sum, r) => sum + (r.price || 0) + (r.tax || 0),
            0
          );
          notifs.push({
            id: 'upcoming-renewals',
            type: 'renewal',
            icon: Clock,
            title: 'Renewals This Week',
            message: `${upcomingRenewals.length} renewal${upcomingRenewals.length > 1 ? 's' : ''} ($${total.toFixed(2)})`,
            severity: 'info',
            link: '/expenses',
          });
        }
      }

      // Check for today's sales
      if (prefs.emailSales) {
        // Use local date instead of UTC to match how sales are stored
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        const { data: todaySales, error } = await supabase
          .from('sales')
          .select('sale_amount, sale_date')
          .eq('user_id', currentUser.uid)
          .eq('sale_date', today);

        if (!error && todaySales && todaySales.length > 0) {
          const total = todaySales.reduce((sum, s) => sum + (s.sale_amount || 0), 0);
          notifs.push({
            id: 'today-sales',
            type: 'sales',
            icon: Receipt,
            title: 'Sales Today',
            message: `${todaySales.length} sale${todaySales.length > 1 ? 's' : ''} ($${total.toFixed(2)})`,
            severity: 'success',
            link: '/sales',
          });
        }
      }

      // Monthly summary bell entry — shown during the FIRST WEEK of each
      // new month (days 1-7) so the user has a window to see last month's
      // recap in the bell, paralleling the email digest the cron sends on
      // day 1. After day 7 we get out of the way; users can still go to
      // the dashboard for full numbers.
      if (prefs.monthlySummaryMode !== 'off') {
        const today = new Date();
        const dayOfMonth = today.getDate();
        if (dayOfMonth <= 7) {
          // Resolve previous month's [start, end] in local time so the
          // bounds match how sales are stored (sale_date is YYYY-MM-DD).
          const firstThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const firstPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastPrevMonth = new Date(firstThisMonth.getTime() - 1);
          const startStr = firstPrevMonth.toISOString().split('T')[0];
          const endStr = lastPrevMonth.toISOString().split('T')[0];
          const monthLabel = firstPrevMonth.toLocaleDateString('en-US', { month: 'long' });

          const { data: lastMonthSales, error: msErr } = await supabase
            .from('sales')
            .select('sale_amount, items')
            .eq('user_id', currentUser.uid)
            .gte('sale_date', startStr)
            .lte('sale_date', endStr);

          if (!msErr && lastMonthSales && lastMonthSales.length > 0) {
            const revenue = lastMonthSales.reduce(
              (sum, s) => sum + (s.sale_amount || 0),
              0
            );
            // Same cost basis the existing weekly report uses (item-level
            // purchasePrice + taxes + shipping). Approximate — the real
            // email digest pulls from sale_items for a tighter number.
            const profit = lastMonthSales.reduce((sum, sale) => {
              const saleAmount = parseFloat(sale.sale_amount) || 0;
              const items = sale.items || [];
              const totalCost = items.reduce((costSum, item) => {
                const purchasePrice = parseFloat(item.purchasePrice) || parseFloat(item.purchase_price) || 0;
                const taxes = parseFloat(item.taxes) || 0;
                const shipping = parseFloat(item.shipping) || 0;
                return costSum + purchasePrice + taxes + shipping;
              }, 0);
              return sum + (saleAmount - totalCost);
            }, 0);

            notifs.push({
              id: 'monthly-summary',
              type: 'monthly',
              icon: FileBarChart,
              title: `${monthLabel} Summary`,
              // Bell shows GROSS profit (revenue - COGS, before expenses)
              // — quick at-a-glance number. The emailed digest separates
              // Gross vs Net so the user can see the full math.
              message: `${lastMonthSales.length} sales · $${revenue.toFixed(2)} revenue · $${profit.toFixed(2)} gross profit`,
              severity: profit >= 0 ? 'success' : 'warning',
              link: '/dashboard',
            });
          }
        }
      }

      // Check for weekly report (show on Sunday/Monday)
      if (prefs.emailReports && isReportDay()) {
        const weekStart = getWeekStart();
        const weekStartStr = weekStart.toISOString();

        // Get last week's sales
        const { data: weekSales, error: weekError } = await supabase
          .from('sales')
          .select('sale_amount, items')
          .eq('user_id', currentUser.uid)
          .gte('created_at', weekStartStr);

        if (!weekError && weekSales) {
          const totalRevenue = weekSales.reduce((sum, s) => sum + (s.sale_amount || 0), 0);
          // Calculate profit: sale_amount - total cost of items (purchasePrice + taxes + shipping)
          const totalProfit = weekSales.reduce((sum, sale) => {
            const saleAmount = parseFloat(sale.sale_amount) || 0;
            const items = sale.items || [];
            const totalCost = items.reduce((costSum, item) => {
              const purchasePrice = parseFloat(item.purchasePrice) || parseFloat(item.purchase_price) || 0;
              const taxes = parseFloat(item.taxes) || 0;
              const shipping = parseFloat(item.shipping) || 0;
              return costSum + purchasePrice + taxes + shipping;
            }, 0);
            return sum + (saleAmount - totalCost);
          }, 0);
          const salesCount = weekSales.length;

          // Only show if there were sales last week
          if (salesCount > 0) {
            notifs.push({
              id: 'weekly-report',
              type: 'report',
              icon: FileBarChart,
              title: 'Weekly Summary',
              message: `${salesCount} sales, $${totalRevenue.toFixed(2)} revenue, $${totalProfit.toFixed(2)} profit`,
              severity: 'info',
              link: '/reports',
            });
          }
        }
      }

      // Filter out session-dismissed notifications
      const filteredNotifs = notifs.filter(n => !sessionDismissed[n.id]);
      setNotifications(filteredNotifs);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, sessionDismissed, dismissedLoaded]);

  // Initial fetch and periodic refresh
  useEffect(() => {
    fetchNotifications();
    // Refresh every 5 minutes as backup
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Real-time subscriptions for live updates
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Subscribe to sales table changes
    const salesSubscription = supabase
      .channel('sales-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sales',
        },
        () => {
          // Clear only the sales dismissal so it can reappear with updated data
          setSessionDismissed(prev => {
            const { 'today-sales': _, ...rest } = prev;
            if (currentUser?.uid) {
              saveDismissed(currentUser.uid, rest);
            }
            return rest;
          });
          fetchNotifications();
        }
      )
      .subscribe();

    // Subscribe to inventory table changes
    const inventorySubscription = supabase
      .channel('inventory-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'inventory',
        },
        () => {
          // Just refetch - the stale count logic will determine if notification should show
          // Only clear dismissal if there are NEW stale items (handled in fetchNotifications)
          fetchNotifications();
        }
      )
      .subscribe();

    // Subscribe to package_notifications inserts so a Shippo update flips
    // the bell within seconds — no refresh, no 5-min wait. Filter by
    // user_id so the realtime fanout is server-side scoped.
    const packageNotifSubscription = supabase
      .channel('package-notifications-bell')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'package_notifications',
          filter: `user_id=eq.${currentUser.uid}`,
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    // Subscribe to expenses changes so the renewal count updates the
    // moment a user adds/edits a recurring expense (no 5-min lag).
    const expensesSubscription = supabase
      .channel('expenses-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'expenses',
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    // Cleanup subscriptions on unmount
    return () => {
      supabase.removeChannel(salesSubscription);
      supabase.removeChannel(inventorySubscription);
      supabase.removeChannel(packageNotifSubscription);
      supabase.removeChannel(expensesSubscription);
    };
  }, [currentUser?.uid, fetchNotifications]);

  const dismiss = (id) => {
    // Package notifications are persistent DB rows — dismissal needs to
    // hit the database so it sticks across reloads, devices, and tabs.
    // Everything else uses the legacy localStorage path (the count-based
    // notifications like Stale Inventory don't have per-row identity).
    const notif = notifications.find(n => n.id === id);
    if (notif?.type === 'package' && notif.dbId) {
      supabase
        .from('package_notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('id', notif.dbId)
        .then(({ error }) => {
          if (error) console.error('Failed to dismiss package notification:', error.message);
        });
    } else {
      // Track dismissed notification for this session (legacy path)
      setSessionDismissed(prev => ({ ...prev, [id]: true }));
    }
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    // Split: package rows go to DB (bulk dismiss), legacy items to localStorage.
    const hasPackageNotifs = notifications.some(n => n.type === 'package');
    const legacyDismissed = notifications
      .filter(n => n.type !== 'package')
      .reduce((acc, n) => ({ ...acc, [n.id]: true }), {});

    if (hasPackageNotifs && currentUser?.uid) {
      // Dismiss by user_id, not by the fetched ids — the bell only loads the
      // 20 most recent rows, so id-based dismissal left older undismissed
      // rows behind and they'd flow right back in on the next fetch.
      supabase
        .from('package_notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('user_id', currentUser.uid)
        .is('dismissed_at', null)
        .then(({ error }) => {
          if (error) console.error('Failed to bulk-dismiss package notifications:', error.message);
        });
    }
    if (Object.keys(legacyDismissed).length > 0) {
      setSessionDismissed(prev => ({ ...prev, ...legacyDismissed }));
    }
    setNotifications([]);
  };

  const severityColors = {
    info: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    warning: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    success: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  };

  const count = notifications.length;

  // Determine dropdown position based on context
  const getDropdownPosition = () => {
    if (isHeader) return 'top-full right-0 mt-2'; // Header: dropdown goes down
    if (isMobile) return 'bottom-full right-0 mb-3'; // Bottom nav: dropdown goes up
    return 'bottom-full left-0 mb-2'; // Desktop sidebar: dropdown goes up
  };

  return (
    <div ref={menuRef} className="relative">
      {isHeader ? (
        // Header button styling - simple icon button
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 rounded-lg transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
          aria-label="Notifications"
          title={count > 0 ? `${count} notification${count > 1 ? 's' : ''} - Click to view` : 'No new notifications'}
        >
          <Bell className="w-5 h-5" strokeWidth={1.75} />
          {count > 0 && (
            <span className="absolute top-0.5 right-0.5 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      ) : isMobile ? (
        // Mobile bottom nav button styling - matches nav item style
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative flex flex-col items-center justify-center gap-0.5 text-theme-muted hover:text-theme transition-all"
          aria-label="Notifications"
          title={count > 0 ? `${count} notification${count > 1 ? 's' : ''} - Tap to view` : 'No new notifications'}
        >
          <Bell className="w-5 h-5" strokeWidth={1.75} />
          <span className="text-[10px] font-medium leading-tight">Alerts</span>
          {count > 0 && (
            <span className="absolute -top-1 right-0 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      ) : (
        // Desktop button styling
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 rounded-lg text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 transition-colors"
          aria-label="Notifications"
          title={count > 0 ? `${count} notification${count > 1 ? 's' : ''} - Click to view` : 'No new notifications'}
        >
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      )}

      <AnimatePresence>
      {isOpen && (
        <motion.div
          // Direction-aware enter/exit: the menu always slides FROM the
          // trigger. Header bell opens downward (slides down from above);
          // sidebar + mobile bottom-nav bells open upward (slide up from
          // below). transformOrigin anchors the scale to the trigger edge
          // so it reads like the menu is growing out of the bell icon.
          initial={{
            opacity: 0,
            y: isHeader ? -8 : 8,
            scale: 0.97,
          }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{
            opacity: 0,
            y: isHeader ? -8 : 8,
            scale: 0.97,
          }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
            transformOrigin: isHeader
              ? 'top right'
              : isMobile
                ? 'bottom right'
                : 'bottom left',
          }}
          className={`absolute ${getDropdownPosition()} w-64 rounded-lg border shadow-xl z-[2000] overflow-hidden`}
        >
          <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border)' }}>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>Notifications</h3>
            <Link
              // Deep-links to Settings → Notifications via the URL hash
              // that SettingsPage reads on mount (see resolveInitialSection
              // in SettingsPage.js). One click from bell to the picker.
              to="/settings#notifications"
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-md transition-colors hover:bg-[var(--color-card-hover)]"
              style={{ color: 'var(--color-text-muted)' }}
              aria-label="Notification settings"
              title="Manage notification preferences"
            >
              <Settings className="w-4 h-4" />
            </Link>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-2 flex items-start gap-2">
                    <div className="w-7 h-7 rounded-md animate-pulse" style={{ backgroundColor: 'var(--color-border)' }} />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-3/4 rounded animate-pulse" style={{ backgroundColor: 'var(--color-border)' }} />
                      <div className="h-2.5 w-1/2 rounded animate-pulse" style={{ backgroundColor: 'var(--color-border)' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center">
                <Bell className="w-6 h-6 mx-auto mb-1" style={{ color: 'var(--color-text-muted)' }} />
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>No notifications</p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                {notifications.map((notif) => {
                  const Icon = notif.icon;
                  return (
                    <div key={notif.id} className="relative p-2 transition-colors hover:bg-[var(--color-card-hover)]">
                      <Link
                        to={notif.link}
                        onClick={() => setIsOpen(false)}
                        className="flex items-start gap-2"
                      >
                        <div className={`p-1.5 rounded-md ${severityColors[notif.severity]}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0 pr-4">
                          <p className="text-xs font-medium" style={{ color: 'var(--color-text)' }}>{notif.title}</p>
                          <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>{notif.message}</p>
                        </div>
                      </Link>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          dismiss(notif.id);
                        }}
                        className="absolute top-1.5 right-1.5 p-0.5 rounded transition-colors hover:bg-[var(--color-card-hover)]"
                        style={{ color: 'var(--color-text-muted)' }}
                        aria-label="Dismiss"
                        title="Dismiss notification"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-3 py-1.5 border-t" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-muted)' }}>
              <button
                onClick={clearAll}
                className="text-[11px] text-red-600 dark:text-red-400 hover:underline"
              >
                Clear All
              </button>
            </div>
          )}
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
